import React from 'react';
import PropTypes from 'prop-types';
import { isOnSameDate, serializeYesplanAPIDate } from '../../libs/dateTimeUtilities';
import Divider from '../../styled components/Divider';
import {
  ScheduleDateDividerContainer,
  ScheduleDateText,
  ScheduleItem,
  ScheduleItemBullet,
  ScheduleItemDescription,
  ScheduleItemTime,
  SchedulesContainer,
  SchedulesTable,
  Timeslot,
  TimeslotBullet
} from './YPTimeSchedulesTable.style';
import FormatSettingsContext from '../FormatSettingsContext';

/**
 * Renders a Yesplan Schedule object.
 * Based on the schedule entries, a table is constructed.
 * If the entries cross day boundaries, a divider with the date is shown.
 *
 * Default schedule times are indicated with _the red dots_.
 *
 * This component has a `defaultDate` property: when the first schedule entries fall on this date,
 * but subsequent entries fall on subsequent dates, the first date divider is not shown since this is for the default date.
 * If the first schedule entries do not fall on this default date, a date divider is shown on top.
 *
 * When the entries all fall on the same date, the table only displays the times and no date divider is displayed.
 *
 */
export default class YPTimeSchedulesTable extends React.Component {
  renderTimeslotBullet(isStartTime, isEndTime) {
    if (!isStartTime && !isEndTime) return null;
    return (
      <Timeslot>
        <TimeslotBullet
          $start="true"
          $active={isStartTime}
          data-test-isdefaultstart={isStartTime}
        />
        <TimeslotBullet $active={isEndTime} data-test-isdefaultend={isEndTime} />
      </Timeslot>
    );
  }

  renderScheduleItem(entry) {
    const { schedules } = this.props;
    const { timeFormatSetting } = this.context;
    const timePart = schedules.printTimeForScheduleEntry(entry, timeFormatSetting);
    const isDefaultScheduleTime =
      entry.isDefaultScheduleStart || entry.isDefaultScheduleEnd;
    return (
      <ScheduleItem
        key={entry.description + entry.starttime}
        $isDefaultScheduleTime={isDefaultScheduleTime}
        data-test-schedule-item
      >
        <ScheduleItemBullet>
          {this.renderTimeslotBullet(
            entry.isDefaultScheduleStart,
            entry.isDefaultScheduleEnd
          )}
        </ScheduleItemBullet>
        <ScheduleItemTime>{timePart}</ScheduleItemTime>
        <ScheduleItemDescription>{entry.description}</ScheduleItemDescription>
      </ScheduleItem>
    );
  }

  renderDateDivider(dateObject, index) {
    const { defaultDate, schedules } = this.props;
    const { dateFormatSetting } = this.context;
    const dateString = serializeYesplanAPIDate(dateObject);
    /* Do not render a date divider if the defaultDate is the first one */
    if (!(index === 0 && isOnSameDate(defaultDate, dateObject))) {
      return (
        <tr key={`schedules-${dateString}`} data-test-date-divider={dateString}>
          <td />
          <td>
            <ScheduleDateDividerContainer>
              <Divider />
            </ScheduleDateDividerContainer>
          </td>
          <td tw="pt-4 pb-4 w-full">
            <ScheduleDateDividerContainer>
              <Divider />
              <ScheduleDateText>
                {schedules.printDateForScheduleDayDivider(dateObject, dateFormatSetting)}
              </ScheduleDateText>
            </ScheduleDateDividerContainer>
          </td>
        </tr>
      );
    }
    return null;
  }

  render() {
    const { schedules, defaultDate } = this.props;
    const displayDates = schedules.shouldDisplayDates(defaultDate);
    return (
      <SchedulesContainer>
        <SchedulesTable data-test-list="schedules">
          <tbody>
            {schedules.getSortedDates().map((dateObj, index) => (
              <React.Fragment key={serializeYesplanAPIDate(dateObj)}>
                {displayDates && this.renderDateDivider(dateObj, index)}
                {schedules
                  .getEntriesForDate(dateObj)
                  .map((schedule) => this.renderScheduleItem(schedule))}
              </React.Fragment>
            ))}
          </tbody>
        </SchedulesTable>
      </SchedulesContainer>
    );
  }
}

YPTimeSchedulesTable.contextType = FormatSettingsContext;

YPTimeSchedulesTable.propTypes = {
  schedules: PropTypes.shape({
    shouldDisplayDates: PropTypes.func.isRequired,
    getSortedDates: PropTypes.func.isRequired,
    getEntriesForDate: PropTypes.func.isRequired,
    printTimeForScheduleEntry: PropTypes.func.isRequired,
    printDateForScheduleDayDivider: PropTypes.func.isRequired
  }).isRequired,
  defaultDate: PropTypes.shape({}).isRequired
};
